import { Component, Input, Output, EventEmitter, ViewChild, AfterViewInit, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSort } from "@angular/material/sort";
import { MatPaginator } from "@angular/material/paginator";
import { CommonModule } from '@angular/common';
import { SelectionModel } from '@angular/cdk/collections';
import { SubSink } from 'subsink';
import TeamUser, { AssignedTask } from '@App/models/team-user.d';
import { AssignedCaseTableRow } from '../assigned-cases-table/assigned-cases-table.component';
import { getSlaStatusFromDueDates } from '@Utils/sla-utils';
import { getTaskDueStatus } from '@Utils/task-due-utils';

@Component({
    selector: 'team-workload-table',
    templateUrl: './team-workload-table.component.html',
    styleUrl: './team-workload-table.component.scss',
    imports: [SharedModule, MatTableModule, MatButtonModule, MatIconModule, CommonModule]
})
export class TeamWorkloadTableComponent implements OnChanges, AfterViewInit, OnInit, OnDestroy {
    @Input() teamId: string;
    @Input() teamData: TeamUser[] = [];
    @Input() filteredCases: AssignedCaseTableRow[] = [];
    @Input() filteredTasks: any[] = [];
    @Input() isFiltered: boolean = false;
    @Input() activeFilterCount: number = 0;
    @Input() tasksEnabled: boolean = false;
    @Output() selectedTeamMembersChange = new EventEmitter<any[]>();
    private _sort: MatSort;
    private _paginator: MatPaginator;
    private subs = new SubSink();
    private _isReselecting = false;

    @ViewChild(MatSort)
    set sort(sort: MatSort) {
        this._sort = sort;
        this.assignTableFeatures();
    }
    get sort(): MatSort {
        return this._sort;
    }

    @ViewChild(MatPaginator)
    set paginator(paginator: MatPaginator) {
        this._paginator = paginator;
        this.assignTableFeatures();
    }
    get paginator(): MatPaginator {
        return this._paginator;
    }

    displayedColumns: string[] = ['selection', 'Team_Member', 'Cases'];
    workloadData = new MatTableDataSource<any>();

    selection: SelectionModel<any> = new SelectionModel<any>(true, []);

    constructor() {
        this.selection = new SelectionModel<any>(true, []);
    }

    ngOnInit(): void {
        this.subs.sink = this.selection.changed.subscribe(() => {
            if (!this._isReselecting) {
                this.selectedTeamMembersChange.emit(this.selection.selected);
            }
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    ngOnChanges(changes?: SimpleChanges): void {
        if (changes?.['teamId'] && !changes['teamId'].firstChange) {
            this.resetSort();
        }
        this.updateDisplayedColumns();
        if (!this.teamData) {
            return;
        }
        this.workloadData = new MatTableDataSource<any>(this.buildWorkloadRows());
        this.assignTableFeatures();
        this.reselectRows();
    }

    private resetSort(): void {
        if (!this._sort) {
            return;
        }
        this._sort.active = '';
        this._sort.direction = '';
        this._sort.sortChange.emit({ active: '', direction: '' });
    }

    private updateDisplayedColumns(): void {
        const columns = ['selection', 'Team_Member', 'Cases'];
        if (this.tasksEnabled) {
            columns.push('Tasks');
        }
        this.displayedColumns = columns;
    }
    
    private buildWorkloadRows(): any[] {
        return this.teamData.map(user => {
            const row: any = { ...user };

            const taskCounts = this.calculateTaskDueCounts(user.tasksAssigned);
            row.tasksPastDue = taskCounts.pastDue;
            row.tasksDueToday = taskCounts.dueToday;
            row.tasksDueTomorrow = taskCounts.dueTomorrow;

            if (this.isFiltered) {
                const userCases = this.filteredCases.filter(c => c.assignedToUserId === user.id);
                row.filteredCasesCount = userCases.length;
                const userTasks = (this.filteredTasks || []).filter(t => t.assignedToUserId === user.id);
                row.filteredTasksCount = userTasks.length;
                let fPastDue = 0, fDueToday = 0, fDueTomorrow = 0;
                for (const c of userCases) {
                    const dates = (c.queue || []).map(q => q.dueDate).filter(d => !!d);
                    const statuses = getSlaStatusFromDueDates(dates);
                    if (statuses.includes('pastDue')) fPastDue++;
                    if (statuses.includes('dueToday')) fDueToday++;
                    if (statuses.includes('dueTomorrow')) fDueTomorrow++;
                }
                row.filteredPastDue = fPastDue;
                row.filteredDueToday = fDueToday;
                row.filteredDueTomorrow = fDueTomorrow;

                const filteredTaskCounts = this.calculateTaskDueCounts(userTasks);
                row.filteredTasksPastDue = filteredTaskCounts.pastDue;
                row.filteredTasksDueToday = filteredTaskCounts.dueToday;
                row.filteredTasksDueTomorrow = filteredTaskCounts.dueTomorrow;
            }
            return row;
        });
    }

    private calculateTaskDueCounts(tasks: AssignedTask[]): { pastDue: number; dueToday: number; dueTomorrow: number } {
        let pastDue = 0, dueToday = 0, dueTomorrow = 0;
        for (const task of tasks || []) {
            if (!task.followUpDate) continue;
            const status = getTaskDueStatus(task.followUpDate);
            if (status === 'pastDue') pastDue++;
            else if (status === 'dueToday') dueToday++;
            else if (status === 'dueTomorrow') dueTomorrow++;
        }
        return { pastDue, dueToday, dueTomorrow };
    }

    ngAfterViewInit(): void {
        this.assignTableFeatures();
    }

    assignTableFeatures(): void {
        if (this.workloadData && this._sort && this._paginator) {
            this.workloadData.sort = this._sort;
            this.workloadData.sortingDataAccessor = (item, property) => {
                switch (property) {
                    case 'Team_Member': return item.userName.toLowerCase();
                    case 'Cases': return this.isFiltered ? (item.filteredCasesCount ?? 0) : item.casesAssigned.length;
                    case 'Tasks': return this.isFiltered ? (item.filteredTasksCount ?? 0) : item.tasksAssigned.length;
                    case 'Past_Due': return this.isFiltered ? (item.filteredPastDue ?? 0) : item.pastDue;
                    case 'Due_Today': return this.isFiltered ? (item.filteredDueToday ?? 0) : item.dueToday;
                    case 'Due_Tomorrow': return this.isFiltered ? (item.filteredDueTomorrow ?? 0) : item.dueTomorrow;
                    default: return '';
                }                    
            }
            this.workloadData.paginator = this._paginator;
        }
    }

    reselectRows(): void {
        this._isReselecting = true;
        const selectedUserIds = this.selection.selected.map(user => user.id);
        this.selection.clear();
        if (selectedUserIds.length > 0) {
            const itemsToReselect = this.workloadData.data.filter(user => 
                selectedUserIds.includes(user.id)
            );
            itemsToReselect.forEach(item => this.selection.select(item));
        }
        this._isReselecting = false;
    }
}